<template>
    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('imageScale')"
        tooltip="Renders the banner at a multiple of its template size. A 300x250 template at 4x produces a 1200x1000 image."
    >
        <h4>Image size</h4>

        <template #input>
            <Select
                :model-value="imageScale"
                data-testid="download-modal__image-scale-select"
                @onChange="onImageScaleChange"
            >
                <Option
                    v-for="scale in IMAGE_SCALES"
                    :key="scale"
                    :value="scale"
                    :label="`${scale}x`"
                    :disabled="scale > maxImageScale"
                    :data-testid="`download-modal__image-scale-select__option-${scale}x`"
                >
                    <Tooltip
                        v-if="scale > maxImageScale"
                        transfer
                        placement="right"
                        class="image-scale-option"
                        transfer-class-name="image-scale-option__tooltip"
                        :max-width="240"
                        :content="disabledImageScaleReason"
                        :data-testid="`download-modal__image-scale-select__option-${scale}x__tooltip`"
                    >
                        {{ scale }}x
                    </Tooltip>

                    <template v-else>{{ scale }}x</template>
                </Option>
            </Select>
        </template>
    </DownloadOptionWrapper>

    <Row v-if="hasExportOptionSetting('dcProfileId')">
        <Col span="16">
            <h4>Profile ID</h4>
            <p class="tooltip">
                Before downloading your ads, set up your campaign feed in <strong>Google Studio</strong> to get your
                <strong>Profile ID</strong> and add it here.
            </p>
            <p class="error">{{ dcProfileIdError }}</p>
        </Col>
        <Col span="8">
            <Input
                v-model="dcProfileId"
                type="number"
                placeholder="Google Studio Profile ID"
                data-testid="download-modal__dc-profile-id-input"
            />
        </Col>
    </Row>

    <Divider v-if="hasExportOptionSetting('dcProfileId')" size="small" />

    <DownloadOptionWrapper v-if="hasExportOptionSetting('ftClickTagCount')" tooltip="Flashtalking Click tag count">
        <Checkbox v-model="ftClickTagCount.enabled" data-testid="download-modal__click-tag-count-checkbox">
            <h4>Click Tag Count</h4>
        </Checkbox>

        <template #input>
            <Input
                v-model="ftClickTagCount.value"
                data-testid="download-modal__click-tag-count-input"
                class="small-input"
            />
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('ftBrowserExclusions')"
        tooltip="Comma separated list of browsers"
    >
        <Checkbox v-model="ftBrowserExclusions.enabled" data-testid="download-modal__browser-exclusions-checkbox">
            <h4>Browser Exclusions</h4>
        </Checkbox>
        <template #input>
            <Input v-model="ftBrowserExclusions.value" data-testid="download-modal__browser-exclusions-input" />
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('optimiseTextFiles')"
        tooltip="Minify HTML, CSS and JS files used by the template"
    >
        <Checkbox v-model="optimiseTextFiles" data-testid="download-modal__optimise-files-checkbox">
            <h4>Optimise Files</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('optimiseFontFiles')"
        tooltip="Remove unused glyphs from font files used in the template"
    >
        <Checkbox v-model="optimiseFontFiles" data-testid="download-modal__optimise-fonts-checkbox">
            <h4>Optimise Fonts</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('optimiseImages')"
        tooltip="This may result in color profile changes"
    >
        <Checkbox v-model="optimiseImages" data-testid="download-modal__optimise-images-checkbox">
            <h4>Optimise Images</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('includeBackupImage')"
        tooltip="Include backup images in the download"
    >
        <Checkbox v-model="includeBackupImage" data-testid="download-modal__backup-images-checkbox">
            <h4>Include backup images</h4>
        </Checkbox>
    </DownloadOptionWrapper>
</template>

<script lang="ts" setup>
import { computed, watch } from "vue";
import {
    EXPORT_CONFIGS,
    IMAGE_SCALES,
    type ExportOptionConfig,
    type ExportOptionSetting,
    type ExportFormatType,
    type ImageScale
} from "shared-utils/enums/exports";
import { MAX_SCREENSHOT_DIMENSION_PX } from "shared-utils/utils/imageScale";

import DownloadOptionWrapper from "@/components/Download/DownloadOptionWrapper.vue";
import { useDownloadHTMLZip } from "@/composables/download/useDownloadHTMLZip";

const { exportFormat, maxImageScale = IMAGE_SCALES[IMAGE_SCALES.length - 1] } = defineProps<{
    exportFormat: ExportFormatType;
    maxImageScale?: ImageScale;
}>();

const {
    optimiseFontFiles,
    optimiseTextFiles,
    optimiseImages,
    includeBackupImage,
    ftBrowserExclusions,
    ftClickTagCount,
    dcProfileId,
    dcProfileIdError,
    imageScale,

    resetDownloadOptions
} = useDownloadHTMLZip();

watch(
    () => exportFormat,
    () => {
        resetDownloadOptions();
    }
);

// The selection survives a change of deliverables, so a scale that was valid a moment ago can
// become unreachable. Pull it back down rather than silently submitting a value the backend clamps.
watch(
    () => maxImageScale,
    max => {
        if (imageScale.value > max) {
            imageScale.value = max;
        }
    }
);

// A greyed out option with no explanation reads as a bug, so spell out the dimension limit that
// gates it.
const disabledImageScaleReason = computed(() => {
    return `The selected template sizes only support up to ${maxImageScale}x — a higher scale would render past the ${MAX_SCREENSHOT_DIMENSION_PX}px limit.`;
});

function onImageScaleChange(value: ImageScale) {
    imageScale.value = value;
}

function hasExportOptionSetting(setting: ExportOptionSetting): boolean {
    const exportConfig: ExportOptionConfig = EXPORT_CONFIGS[exportFormat];

    return exportConfig.settings?.includes(setting) ?? false;
}
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;
.tooltip {
    font-size: 12.5px;
}

// Stretch the tooltip trigger across the option so the whole row is hoverable.
.image-scale-option,
.image-scale-option :deep(.ivu-tooltip-rel) {
    display: block;
}

.error {
    color: $error-color;
    font-size: 10px;
    height: 10px;
}
</style>

<style lang="scss">
// The popper is teleported to <body>, so it is out of reach of the scoped styles above.
// view-ui-plus defaults the tooltip to `white-space: nowrap`, which spills a sentence this long
// outside of its own background; `max-width` on the Tooltip opts into wrapping, and these override
// the justified, mid-word breaking the library pairs with it.
.image-scale-option__tooltip {
    .ivu-tooltip-inner-with-width {
        text-align: left;
        word-break: break-word;
    }
}
</style>
